import { useParams } from 'react-router-dom';
import ImageWithOverlay from '../../components/Shared/ImageWithOverlay';
import DetailsSection from '../../components/DetailsSection/DetailsSection';
import ParticipantForm from '../../components/ParticipantForm/ParticipantForm';
import useQuestionnaire from '../../hooks/useQuestionnaire';
import { Clock, List, Tag, FileText } from 'lucide-react';

const QuestionnaireDetails = () => {
  const { id } = useParams();
  const { questionnaire, loading, error } = useQuestionnaire(id);

  if (loading) {
    return (
      <div className="container mx-auto my-4">
        <div className="flex flex-col md:flex-row theme-card max-w-4xl mx-auto overflow-hidden rounded-xl">
          <div className="flex flex-col lg:flex-row w-full">
            {/* Image Skeleton */}
            <div className="lg:w-2/5 aspect-[4/3] bg-gray-200 animate-pulse" />
            
            {/* Content Skeleton */}
            <div className="flex-1 p-4 lg:p-6">
              <div className="space-y-4">
                {/* Details Skeleton */}
                <div className="grid gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="theme-card rounded-xl p-3">
                      <div className="flex items-start space-x-3 space-x-reverse">
                        <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2 animate-pulse" />
                          <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Form Skeleton */}
                <div className="theme-card p-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i}>
                        <div className="h-3 bg-gray-200 rounded w-1/4 mb-2 animate-pulse" />
                        <div className="h-10 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                    <div className="h-10 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-error bg-error p-4 rounded-lg">
          {error.message}
        </div>
      </div>
    );
  }

  const details = [
    {
      icon: <FileText className="w-5 h-5" />,
      label: 'توضیحات',
      value: questionnaire.description,
    },
    {
      icon: <Clock className="w-5 h-5" />,
      label: 'زمان پاسخگویی',
      value: `${Math.floor(questionnaire.response_time_limit / 60)} دقیقه`,
    },
    {
      icon: <List className="w-5 h-5" />,
      label: 'تعداد سوالات',
      value: `${questionnaire.desired_question_count} سوال`,
    },
    ...(questionnaire.tags && questionnaire.tags.length > 0
      ? [{
        icon: <Tag className="w-5 h-5" />,
        label: 'تگ‌ها',
        value: questionnaire.tags.map(tag => tag.name).join('، '),
      }]
      : []),
  ];

  return (
    <div>
      <div className="container mx-auto my-4">
        <div className="flex flex-col md:flex-row theme-card max-w-4xl mx-auto overflow-hidden rounded-xl transition-shadow duration-300 hover:shadow-xl">
          <div className="flex flex-col lg:flex-row w-full">
              <ImageWithOverlay
                src={questionnaire.image}
                title={questionnaire.title}
                subtitle={questionnaire.description}
              />
            <div className="flex-1 p-4 lg:p-6">
              <div className="space-y-4">
                <DetailsSection details={details} />
                <ParticipantForm questionnaireId={id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireDetails;